/**
 * The agency workspace, laid out like AgencyAnalytics: the icon rail on the
 * left and one large panel for the open area or client.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { signOut, type Organisation } from '../api.ts';
import { reportsApi, type Catalogue, type Client, type Template } from '../reports/reports-api.ts';
import { finishGoogleConnect, isGoogleReturn } from './data/google-connect.ts';
import { AgencySettings, AgencyTemplates } from './AgencyPages.tsx';
import { McpPage } from './mcp/McpPage.tsx';
import { AgencyDocuments } from './rollups/AgencyDocuments.tsx';
import { AssistantLauncher } from '../assistant/AssistantLauncher.tsx';
import { KpiPage } from '../kpis/KpiPage.tsx';
import { IntegrationsPage } from './data/IntegrationsPage.tsx';
import { ClientPage } from './ClientPage.tsx';
import { clientsApi } from './clients-api.ts';
import { ClientsPage, initialsOf } from './ClientsPage.tsx';
import { TrialBanner } from './GettingStarted.tsx';
import { IconRail } from './IconRail.tsx';
import { useRoute } from './routes.ts';

export function AgencyApp({ organisation }: { organisation: Organisation }): ReactNode {
  const route = useRoute();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [catalogue, setCatalogue] = useState<{
    catalogue: Catalogue;
    templates: Template[];
  } | null>(null);

  const loadClients = useCallback(() => {
    clientsApi
      .list(organisation.id)
      .then(setClients)
      .catch(() => setClients([]));
  }, [organisation.id]);
  useEffect(loadClients, [loadClients]);
  const [connectNotice, setConnectNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // Back from Google's consent page: finish the connection, then show Data.
  useEffect(() => {
    if (!isGoogleReturn()) return;
    void finishGoogleConnect().then((problem) => {
      setConnectNotice(
        problem
          ? { ok: false, text: problem }
          : {
              ok: true,
              text: 'Google Analytics is connected. Attach a property to a client in its Data tab.',
            },
      );
      window.history.replaceState({}, '', '/#/data');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    });
  }, []);
  useEffect(() => {
    reportsApi
      .catalogue(organisation.id)
      .then(setCatalogue)
      .catch(() => setCatalogue(null));
  }, [organisation.id]);

  const main = (): ReactNode => {
    switch (route.area) {
      case 'client': {
        const client = clients?.find((item) => item.id === route.clientId);
        if (!clients) return <p className="ag-loading">Loading…</p>;
        if (!client)
          return (
            <p className="ag-loading">
              This client no longer exists. <a href="#/clients">Back to clients</a>
            </p>
          );
        return (
          <ClientPage
            key={client.id}
            organisationId={organisation.id}
            organisationName={organisation.name}
            client={client}
            tab={route.tab}
            documentId={route.documentId}
            explore={route.explore}
            onClientsChanged={loadClients}
          />
        );
      }
      case 'reports':
      case 'rollups':
        return (
          <AgencyDocuments
            key={route.area}
            area={route.area}
            organisationId={organisation.id}
            clients={clients ?? []}
            catalogue={catalogue?.catalogue ?? null}
            templates={catalogue?.templates ?? []}
            documentId={route.documentId ?? null}
          />
        );
      case 'data':
        return <IntegrationsPage organisationId={organisation.id} notice={connectNotice} />;
      case 'templates':
        return <AgencyTemplates templates={catalogue?.templates ?? []} />;
      case 'settings':
        return <AgencySettings organisationId={organisation.id} />;
      case 'kpis':
        return (
          <KpiPage
            organisationId={organisation.id}
            clients={clients ?? []}
            catalogue={catalogue?.catalogue ?? null}
            clientId={null}
          />
        );
      case 'mcp':
        return <McpPage organisationId={organisation.id} clients={clients ?? []} />;
      default:
        return (
          <ClientsPage
            organisationId={organisation.id}
            clients={clients}
            catalogue={catalogue?.catalogue ?? null}
            onChanged={loadClients}
          />
        );
    }
  };

  return (
    <div className="ag">
      <IconRail
        organisationId={organisation.id}
        route={route}
        initials={initialsOf(organisation.name)}
        onSignOut={() => {
          signOut();
          window.location.reload();
        }}
      />
      <main className="ag-main">
        <TrialBanner organisation={organisation} />
        {main()}
      </main>
      <AssistantLauncher
        organisationId={organisation.id}
        permissions={organisation.permissions}
        client={
          route.area === 'client'
            ? (clients?.find((item) => item.id === route.clientId) ?? null)
            : null
        }
      />
    </div>
  );
}
